import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, Megaphone, Store, MapPin, Package, Bike, ShoppingBag,
  Laptop, Car, Home, Sofa, Dumbbell, Shirt, Gamepad2, Wrench, ShieldCheck, Truck, CreditCard,
} from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { categories, banners } from '../mock/mockData';
import { productsAPI, adsAPI } from '../services/api';

const ICON_MAP = {
  Laptop, Car, Home, Sofa, Dumbbell, Shirt, Gamepad2, Wrench,
};

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [sponsoredProducts, setSponsoredProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  // Hero carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => { loadProducts(); loadSponsoredProducts(); loadStores(); }, []);

  // Auto-advance hero every 4s
  useEffect(() => {
    if (banners.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const goSlide = useCallback((dir) => {
    clearInterval(intervalRef.current);
    setCurrentSlide(prev => (prev + dir + banners.length) % banners.length);
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % banners.length);
    }, 4000);
  }, []);

  const loadProducts = async () => {
    try {
      const data = await productsAPI.getAll();
      setAllProducts(data);
      setFeaturedProducts(data.filter(p => p.featured).slice(0, 4));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadSponsoredProducts = async () => {
    try {
      const data = await adsAPI.getAdsForFeed({ limit: 4 });
      setSponsoredProducts(data);
      data.forEach(p => { if (p.campaign_id) adsAPI.recordImpression(p.campaign_id).catch(() => {}); });
    } catch { setSponsoredProducts([]); }
  };

  const loadStores = async () => {
    try {
      const prods = await productsAPI.getAll();
      const map = {};
      prods.forEach(p => {
        const sid = p.seller_id || p.sellerId;
        if (!sid) return;
        if (!map[sid]) map[sid] = { id: sid, name: p.seller || 'Tienda', products: 0, location: p.seller_location || null };
        map[sid].products++;
      });
      setStores(Object.values(map).sort((a, b) => b.products - a.products).slice(0, 6));
    } catch { setStores([]); }
  };

  const banner = banners[currentSlide] || banners[0];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* ═══ HERO BANNER — Auto-advance ═══ */}
      <div className="bg-white py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative" data-testid="hero-carousel">
            {/* Slide */}
            <div className="relative h-44 sm:h-64 lg:h-80 rounded-xl overflow-hidden">
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover transition-opacity duration-700"
                key={currentSlide}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex items-center">
                <div className="text-white px-6 sm:px-12">
                  <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-1 sm:mb-3">{banner.title}</h2>
                  <p className="text-base sm:text-xl lg:text-2xl mb-3 sm:mb-6">{banner.subtitle}</p>
                  <Button size="sm" className="bg-zl-orange hover:bg-zl-orange-600 text-white sm:text-base sm:px-6 sm:py-3" onClick={() => navigate(banner.link)}>
                    {banner.cta}
                  </Button>
                </div>
              </div>
            </div>

            {/* Arrows */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={() => goSlide(-1)}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
                  data-testid="hero-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => goSlide(1)}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
                  data-testid="hero-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { clearInterval(intervalRef.current); setCurrentSlide(i); intervalRef.current = setInterval(() => setCurrentSlide(p => (p + 1) % banners.length), 4000); }}
                  className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
                  data-testid={`hero-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sponsored */}
      {sponsoredProducts.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 py-6 sm:py-8 border-t border-b border-amber-200/60" data-testid="sponsored-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-amber-200/70">
                <Megaphone className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Recomendados para ti</h2>
                <p className="text-[10px] sm:text-xs text-amber-700 font-medium uppercase tracking-wide">Publicidad</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {sponsoredProducts.slice(0, 4).map((product) => (
                <ProductCard key={product._id || product.id} product={product} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ CATEGORIES — Horizontal Icon Slider ═══ */}
      <div className="bg-white py-5 sm:py-8 mt-4 sm:mt-6">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-lg sm:text-xl font-bold mb-4" data-testid="categories-title">Categorias</h2>
          <div
            className="flex gap-3 sm:gap-5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
            data-testid="categories-slider"
          >
            {categories.map((cat) => {
              const IconComp = ICON_MAP[cat.icon] || Package;
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/category/${cat.name}`)}
                  className="flex flex-col items-center gap-2 min-w-[72px] sm:min-w-[80px] snap-start group"
                  data-testid={`category-${cat.id}`}
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-orange-50 group-hover:ring-2 group-hover:ring-orange-300 transition-all">
                    <IconComp className="w-6 h-6 sm:w-7 sm:h-7 text-gray-600 group-hover:text-orange-500 transition-colors" />
                  </div>
                  <span className="text-[11px] sm:text-xs text-gray-700 font-medium text-center leading-tight group-hover:text-orange-600 transition-colors">
                    {cat.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stores Section */}
      {stores.length > 0 && (
        <div className="bg-white py-6 sm:py-8 mt-4 sm:mt-6">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <Store className="w-6 h-6 text-zl-orange" />
                <h2 className="text-xl sm:text-2xl font-bold">Tiendas</h2>
              </div>
              <Button variant="ghost" onClick={() => navigate('/store-config')} className="text-zl-orange hover:text-zl-orange-600 text-sm sm:text-base">
                <ShoppingBag className="w-4 h-4 mr-1" /> Abre tu tienda <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {stores.map(store => (
                <Card key={store.id} className="p-4 hover:shadow-lg transition-all cursor-pointer text-center hover:-translate-y-0.5" onClick={() => navigate(`/store/${store.id}`)}>
                  <div className="w-14 h-14 mx-auto bg-zl-orange-100 rounded-full flex items-center justify-center mb-3">
                    <Store className="w-7 h-7 text-zl-orange" />
                  </div>
                  <p className="font-semibold text-sm truncate">{store.name}</p>
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 mt-1">
                    <Package className="w-3 h-3" /> {store.products} productos
                  </div>
                  {store.location && (
                    <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-0.5">
                      <MapPin className="w-3 h-3" /> {store.location}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Banners */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-zl-orange to-amber-600 rounded-xl p-6 sm:p-8 text-white cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate('/seller/products')} data-testid="cta-open-store">
            <ShoppingBag className="w-10 h-10 mb-3 opacity-80" />
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Abre tu tienda</h3>
            <p className="text-sm text-white/80 mb-4">Vende tus productos a miles de compradores.</p>
            <span className="inline-flex items-center text-sm font-semibold bg-white/20 px-4 py-2 rounded-full">Comenzar <ChevronRight className="w-4 h-4 ml-1" /></span>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-6 sm:p-8 text-white cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate('/ads')} data-testid="cta-ads">
            <Megaphone className="w-10 h-10 mb-3 opacity-80" />
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Zona Libre Ads</h3>
            <p className="text-sm text-white/80 mb-4">Impulsa tus ventas con publicidad segmentada.</p>
            <span className="inline-flex items-center text-sm font-semibold bg-white/20 px-4 py-2 rounded-full">Anunciate <ChevronRight className="w-4 h-4 ml-1" /></span>
          </div>
          <div className="bg-gradient-to-br from-zl-brown to-amber-900 rounded-xl p-6 sm:p-8 text-white cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate('/delivery/register')} data-testid="cta-delivery">
            <Bike className="w-10 h-10 mb-3 opacity-80" />
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Se repartidor</h3>
            <p className="text-sm text-white/80 mb-4">Gana dinero haciendo entregas con tu moto.</p>
            <span className="inline-flex items-center text-sm font-semibold bg-white/20 px-4 py-2 rounded-full">Registrate <ChevronRight className="w-4 h-4 ml-1" /></span>
          </div>
        </div>
      </div>

      {/* Featured Products */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold">Ofertas destacadas</h2>
          <Button variant="ghost" onClick={() => navigate('/search?sort=offers')} className="text-zl-orange hover:text-zl-orange-600">Ver todas <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
        {loading ? <div className="text-center py-12">Cargando...</div> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {(featuredProducts.length > 0 ? featuredProducts : allProducts.slice(0, 4)).map(p => (
              <ProductCard key={p._id || p.id} product={p} />
            ))}
          </div>
        )}
      </div>

      {/* All Products */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Basado en tu ultima visita</h2>
        {!loading && allProducts.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {allProducts.slice(0, 8).map(p => <ProductCard key={p._id || p.id} product={p} />)}
          </div>
        )}
      </div>

      {/* Trust Section */}
      <div className="bg-white py-8 sm:py-12 mt-6 sm:mt-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center">
            <div>
              <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-zl-orange" />
              <h3 className="text-base sm:text-lg font-semibold mb-1">Seguridad</h3>
              <p className="text-sm text-gray-600">Protegemos tus datos y compras</p>
            </div>
            <div>
              <Truck className="w-8 h-8 mx-auto mb-2 text-zl-orange" />
              <h3 className="text-base sm:text-lg font-semibold mb-1">Envio gratis</h3>
              <p className="text-sm text-gray-600">En miles de productos</p>
            </div>
            <div>
              <CreditCard className="w-8 h-8 mx-auto mb-2 text-zl-orange" />
              <h3 className="text-base sm:text-lg font-semibold mb-1">Paga como quieras</h3>
              <p className="text-sm text-gray-600">Multiples metodos de pago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
